import type { ValueType } from "../UserQueryBuilder/types";

import type { Result } from "../Inputs/SelectInput";
import {
	ANNIVERSARY_QUERY_OPTIONS,
	DATE_TYPES,
	DEFAULT_QUERY_OPTIONS,
	NUMERIC_QUERY_OPTIONS,
	NUMERIC_TYPES,
	RELATIVE_DATE_QUERY_OPTIONS,
	STATIC_DATE_QUERY_OPTIONS,
	TEXT_QUERY_OPTIONS,
	TEXT_TYPES,
} from "./constants";

export interface IQueryField {
	_id: string;
	name: string;
	type?: string;
	options?: IQueryField[] | string[];
}

export interface RelativeDateValue {
	length: number;
	unit: "days" | "months" | "years";
}

export interface QueryValue {
	label: string;
	value: number | string | Date | RelativeDateValue | boolean;
}

export interface IQuery {
	field: IQueryField;
	comparator: IComparator;
	queryValue: QueryValue;
}

export interface IComparator extends Result {
	label: string;
	value:
		| "eq"
		| "ne"
		| "gt"
		| "gte"
		| "lt"
		| "lte"
		| "in"
		| "nin"
		| "contains"
		| "notContains"
		| "startsWith"
		| "endsWith"
		| "notStartsWith"
		| "notEndsWith"
		| "before"
		| "after"
		| "after_anniversary"
		| "before_anniversary"
		| "eq_anniversary";
}

export const getSelectionFieldsAsResults = (selectionFields: IQueryField[] = []) => {
	const results = selectionFields.map((field: IQueryField) => {
		return {
			value: field._id,
			label: field.name,
		} as Result;
	});

	return results;
};

/*
 * this function calculates if the query form is completly filled out
 */
export const calculateIsQueryComplete = (query: IQuery[]) => {
	return query.every((queryItem) => {
		return (
			queryItem.queryValue?.value != null && queryItem.comparator?.value != null && queryItem.field?._id != null
		);
	});
};

export const calculateQueryComparatorOptions = (fieldType: string) => {
	let comparatorOptions = DEFAULT_QUERY_OPTIONS;
	if (NUMERIC_TYPES.includes(fieldType)) {
		comparatorOptions = NUMERIC_QUERY_OPTIONS;
	} else if (TEXT_TYPES.includes(fieldType)) {
		comparatorOptions = TEXT_QUERY_OPTIONS;
	} else if (fieldType === "static") {
		comparatorOptions = STATIC_DATE_QUERY_OPTIONS;
	} else if (fieldType === "relative") {
		comparatorOptions = RELATIVE_DATE_QUERY_OPTIONS;
	} else if (fieldType === "anniversary") {
		comparatorOptions = ANNIVERSARY_QUERY_OPTIONS;
	}
	return comparatorOptions;
};

export const unRegexQueryValue = (queryValue: string, comparator: string) => {
	if (comparator === "startsWith" || comparator === "notStartsWith") {
		return queryValue.substring(1);
	}
	if (comparator === "endsWith" || comparator === "notEndsWith") {
		return queryValue.substring(0, queryValue.length - 1);
	}
	return queryValue;
};

export const getRelativeDateLabel = (relativeDate: RelativeDateValue) => {
	const { length, unit } = relativeDate;
	const unitLabel = unit.charAt(0).toUpperCase() + unit.slice(1);
	return `${length} ${length === 1 ? unitLabel.slice(0, unitLabel.length - 1) : unitLabel} Ago`;
};

export const getAnniversaryDateLabel = (anniversaryDate: RelativeDateValue) => {
	const { length, unit } = anniversaryDate;
	const unitLabel = unit.charAt(0).toUpperCase() + unit.slice(1);
	return `${length} ${length === 1 ? unitLabel.slice(0, unitLabel.length - 1) : unitLabel}`;
};

export const getDefaultComparator = (fieldType: string, isStaticDate = false, isAnniversaryDate = false) => {
	let comparator = DEFAULT_QUERY_OPTIONS[0];
	if (DATE_TYPES.includes(fieldType) && isStaticDate) {
		comparator = STATIC_DATE_QUERY_OPTIONS[0];
	} else if (DATE_TYPES.includes(fieldType) && isAnniversaryDate) {
		comparator = ANNIVERSARY_QUERY_OPTIONS[0];
	} else if (DATE_TYPES.includes(fieldType)) {
		comparator = RELATIVE_DATE_QUERY_OPTIONS[0];
	}

	return comparator;
};

export const getOidValue = (value: ValueType | undefined) => {
	if (value && typeof value === "object") {
		if ("oid" in value) {
			return value.oid;
		}
	}
};

export const getStaticDateValue = (value: ValueType | undefined) => {
	if (value && typeof value === "object") {
		if ("date" in value) {
			return value.date;
		}
	}
};

export const getRelativeDateValue = (value: ValueType | undefined) => {
	if (value && typeof value === "object") {
		if ("relative_date" in value) {
			return {
				relativeDate: value.relative_date,
				relativeDateUnit: value.relative_date_unit,
			};
		}
	}
};

export const getAnniversaryDateValue = (value: ValueType | undefined) => {
	if (value && typeof value === "object") {
		if ("anniversary_date" in value) {
			return {
				anniversaryDate: value.anniversary_date,
				anniversaryDateUnit: value.anniversary_date_unit,
			};
		}
	}
};
